import axios from 'axios';
import { RequestConfig } from '@/../types/interface/config';
import { getCookie, removeCookie, removeStorage, setCookie } from '@lhb/cache';
import { deepCopy, isDef, dateFns, isMobile, matchQuery } from '@lhb/func';
import { URL_REG } from '@lhb/regexp';
import { isApp, isAppAndroid, isAppIOS } from '@/common/utils/tools';
import { getAfferentToken } from '@/common/utils/bridge';
import { login } from '@lhb/bridge';
import { Toast } from 'antd-mobile';
import * as Sentry from '@sentry/react';
import { isLarkEnv, isWxMiniProgram } from '../utils/ways';
import { handleInterceptInLark } from '../utils/lark';
import { v4 } from 'uuid';
import { dispatchNavigate } from '../document-event/dispatch';
import { buildLoginPath } from '../utils/login-callback';

// MockUrl
const mockUrl = 'https://yapi.lanhanba.com/mock/';
// const baseUrl = '/api';

const mainPrefix = `/terra`;
const applicationZeus = `/zeus-api`;
const applicationLcn = `/lcn-api`;
const applicationMirage = `/mirage/api`;
// const applicationLocal = `/api`; // 本地

const errorTokenLen = 20; // token异常时的长度
/**
 * 请求基础配置
 */
const baseConfig: any = {
  needCancel: true,
  isMock: false,
  mockId: 347, // 其他后台mock的id
  needHint: true,
  mockSuffix: '',
  isDelObjEmptyProVal: false, // 是否过滤对象中值为' ' || undefined || null的字段
  errorConfig: {
    needHint: true,
    duration: 2000,
    closable: false,
  },
};

/**
 * 异常处理
 */
class ApiError extends Error {
  code: string;
  constructor(err: { message?: string; msg?: string; code: string }) {
    const msg: string = err.message || err.msg || '';
    super(msg);
    Object.assign(this, err);
    this.code = err.code;
  }
}

/**
 * 获取header
//  * @param version 版本号
 * @param isPost 是否是 Post 请求
 */
export async function getHeader() {
  const path = window?.location?.pathname;

  const config: any = {
    // Accept: 'application/vnd.linhuiba.v' + version + '+json',
    ...(path ? { 'X-Source-Url': path } : {})
  };
  let newToken = getCookie('flow_token');
  try {
    let isShareUrl = false; // 如果shareId参数存在，就代表是处于分享页
    if (matchQuery(window.location.href, 'shareId')) {
      isShareUrl = true;
    } else if (window.parent?.location?.href && matchQuery(window.parent.location.href, 'shareId')) { // 父窗口嵌套
      isShareUrl = true;
    }
    if (isShareUrl) {
      newToken = getCookie('flow_share_token');
    }
  } catch (err) {}
  // flow_token为接入企业微信后与console-pc保持一致, 所加的或者条件都为企微对接单点登录的兼容性处理，从console-pc获取
  // UIMS_TOKEN 是原生加载H5时的token，该值会被混淆。 flow_token是H5项目互相跳转时
  let token = getCookie('UIMS_TOKEN') || newToken; // app加载时的token
  if (isApp()) {
    // app环境内
    if (token) {
      token = token.substring(2, token.length - 8); // app端传过来的cookie是经过处理的
      if (token?.length < errorTokenLen) {
        customEventOfSentry(token, {
          msg: 'getCookie(`UIMS_TOKE`)时获取到非法的toke'
        });
      }
      // else { // 拿到cookie后存在本地
      //   setStorage('ONLY_APP_TOKEN', token);
      // }
      // setCookie('flow_token', token);
      setCookie('insightToken', token);
      config['token'] = token;

      const deviceId = getCookie('DEVICE_ID'); // 目前使用的是app设备推送id
      if (deviceId && deviceId.length) {
        config['deviceId'] = deviceId;
      }
      return config;
    }
    // 部分机型上会有拿不到cookie的时候,主动向app索取token
    // const { uimsToken }: any = await getAfferentToken();
    const { uimsToken, TENANT_ID, EMPLOYEE_ID, DEVICE_ID }: any = await getAfferentToken();
    // if (!uimsToken || uimsToken?.length < errorTokenLen) {
    // 部分接口不需要传token，此时如果未登录是没有token的，存在大量误报，原生友盟埋点了回调结果
    if (uimsToken?.length < errorTokenLen) {
      // const errorMsg = !uimsToken ? 'getToke 桥接事件没有返回uimsToke' : 'getToke 桥接事件获取到非法的toke';
      const errorMsg = 'getToke 桥接事件获取到非法的toke';
      customEventOfSentry(token, {
        msg: errorMsg
      });
    }
    // else { // 拿到后存在本地
    //   setStorage('ONLY_APP_TOKEN', uimsToken);
    // }
    // const onlyInAppToken = getStorage('ONLY_APP_TOKEN');
    const targetToken = uimsToken;
    // if (!onlyInAppToken) {
    //   customEventOfSentry(targetToken, {
    //     msg: '前端兜底的toke也没取到'
    //   });
    // }
    config['token'] = targetToken;
    // setCookie('flow_token', targetToken);
    // 用来sentry上报/大数据埋点抓取
    setCookie('insightToken', targetToken);
    setCookie('TENANT_ID', TENANT_ID);
    setCookie('EMPLOYEE_ID', EMPLOYEE_ID);
    if (DEVICE_ID) {
      setCookie('DEVICE_ID', DEVICE_ID);
      config['deviceId'] = DEVICE_ID;
    }
    return config;
  }

  // 浏览器环境
  const userInfo = getCookie('userInfo');
  if (userInfo) {
    token = JSON.parse(userInfo).uims_token || token;
    setCookie('TENANT_ID', JSON.parse(userInfo).TENANT_ID);
    setCookie('EMPLOYEE_ID', JSON.parse(userInfo).EMPLOYEE_ID);
  }
  if (token) {
    config['token'] = token;
    // 用来sentry上报/大数据埋点抓取
    setCookie('insightToken', token);
  }
  // H5环境下
  let deviceId = getCookie('deviceId');
  if (!deviceId) {
    deviceId = v4();
    setCookie('deviceId', deviceId);
  }
  config['deviceId'] = deviceId;
  return config;
}

/**
 * 合并请求
 * @param extraConfig
 */
export function mergeConfig(extraConfig: any): RequestConfig {
  const _baseConfig = deepCopy(baseConfig);
  // 为了方便 第三个参数为boolean 值时可控制needHint值
  if (typeof extraConfig === 'boolean') {
    _baseConfig.errorConfig.needHint = extraConfig;
    return _baseConfig;
  } else if (typeof extraConfig.needHint === 'boolean') {
    _baseConfig.errorConfig.needHint = extraConfig.needHint;
  }
  return { ..._baseConfig, ...extraConfig };
}

/**
 * 返回配置后的请求URl
 */
export function getRequestUrl(serviceName: string, config: RequestConfig): string {
  // return (config.isMock ? `${mockUrl}${config.mockId || baseConfig.mockId}/api` : baseUrl) + serviceName;
  if (URL_REG.test(serviceName)) return serviceName;
  const { isMock, isZeus, isLcn, isMirage } = config;
  if (process.env.NODE_ENV === 'development' && isMock) { // 防止非开发环境使用 mock 接口数据
    return (
      `${mockUrl}${config.mockId || baseConfig.mockId}${
        config.mockSuffix || baseConfig.mockSuffix
      }` + serviceName
    );
  }
  let prefix = mainPrefix;

  if (isZeus) {
    // 宙斯
    prefix = applicationZeus;
  }

  if (isLcn) {
    // 宙斯
    prefix = applicationLcn;
  }

  if (isMirage) {
    prefix = applicationMirage;
  }

  // if (isLocal) {
  //   prefix = applicationLocal;
  // }
  // if (isCheckSpot) { // 其他应用前缀
  //   prefix = applicationCheckSpot;
  // }
  return `${prefix}${serviceName}`;
}

function customEventOfSentry (token: string, extraConfig?: any) {
  let platform = isMobile() ? 'h5' : '未知系统';
  if (isApp()) {
    platform = isAppIOS() ? 'ios' : (isAppAndroid() ? 'android' : '未知的移动设备'); // 平台
  }
  const customEvent: any = {
    message: '401',
    level: 'error', // 可以是 'info', 'warning', 'error'
    extra: {
      // 添加任何额外的详细信息，你希望伴随这个事件
      platform,
      issueStr: token,
      timestamp: dateFns.currentTime('', false), // 时间戳
      ...(extraConfig || {})
    },
  };
  Sentry.captureEvent(customEvent);
};

/**
 * 错误的请求处理
 * @param err
 * @param config
 */
export function fail(err: any, config: RequestConfig) {
  if (axios.isCancel(err)) {
    throw new ApiError({
      code: 'cancel',
      msg: '您取消了请求',
    });
  }
  const needHint = config?.errorConfig?.needHint;
  const duration = config?.errorConfig?.duration;
  // const closable = config?.errorConfig?.showClose || false;
  const errorOptions: any = {
    // type: 'error',
    duration,
    // closable,
  };
  if (err.response) {
    const {
      status,
      data: { errMsg, message },
    } = err.response;
    // 服务端 2022/06/14 版本接口约定
    switch (status) {
      case 401:
        errorOptions.content = '未登录';
        break;
      case 403:
        errorOptions.content = '没有权限';
        break;
      case 500:
        errorOptions.content = '系统错误';
        break;
      default:
        if (needHint) {
          errorOptions.content = errMsg || message;
        }
        break;
    }
    if (errorOptions.content) {
      if (status === 401) {
        if (isApp()) {
          const token = getCookie('UIMS_TOKEN');
          // 原生环境下 sentry上报401场景
          customEventOfSentry(token?.substring(2, token.length - 8));
          // app环境内
          login();
        } else {
          const isQyWx = window.navigator.userAgent.includes('wxwork'); // 判断是否在企业微信环境中
          let isShareUrl = false; // 如果shareId参数存在，就代表是处于分享页
          try {
            if (matchQuery(window.location.href, 'shareId')) {
              isShareUrl = true;
            } else if (window.parent?.location?.href && matchQuery(window.parent.location.href, 'shareId')) { // 父窗口嵌套
              isShareUrl = true;
            }
          } catch (err) {}
          // sentry上报401场景
          const token = isShareUrl ? getCookie('flow_share_token') : getCookie('flow_token');
          customEventOfSentry(token);
          // 如果在企业微信浏览器内退出则跳转到console-pc响应式的登录页
          if (isQyWx) {
            // 需要把跳回PC登录相关的cookie去掉
            removeCookie('flow_token');
            removeCookie('flow_share_token');
            removeStorage('flowLoginToken');
            removeCookie('flow_account_id');
            // window.location.href = process.env.CONSOLE_PC_URL || '/';
            dispatchNavigate(buildLoginPath());
          } else if (isLarkEnv()) { // 飞书环境内
            handleInterceptInLark();
          } else {
            // 小程序环境下如果接口报 401 不跳转到登录，这里的 h5 是给小拇指用的，对于小拇指来说没有账号密码到登录页没有意义，还有个问题是现在的登录页是locationweb2b.lanhanba.com，小程序未绑定这个业务域名也会报错。后续涉及到小程序开发需要额外处理。
            if (!isWxMiniProgram()) {
              dispatchNavigate(buildLoginPath());
            } else { // 如果是小程序情况下，提示错误信息，不跳转到登录页（小拇指专属使用）
              Toast.show(errorOptions);
            }
          }
        }
        isApp() && login();
      } else if (needHint) { // 只有需要提示的时候才提示，不加判断needHint属性是无效的
        Toast.show(errorOptions);
      }
    }
  }
  throw new ApiError(err);
}

/**
 * 成功请求处理
 * remark: 暂时未做处理，预留
 * @param res
 */
export function success(res: any, config: any) {
  const { data } = res;
  if (config.isMock) {
    return data;
  }
  if (isDef(data)) {
    return data;
  } else {
    // AntdMessage.open({
    //   type: 'error',
    //   content: data.msg,
    // });
  }
  throw new ApiError(data);
}

/**
 * 获取 axios 请求配置
 * @param requestConfig
 * @param axiosConfig
 */
export function getAxiosConfig(requestConfig: RequestConfig, axiosConfig: any = {}) {
  const { needCancel = true, headers, customCancelToken } = requestConfig;
  // 需要cancel的时候，就加入cancelToken
  if (customCancelToken) { // 如果设置了手动cancelToken，就使用手动方案
    axiosConfig.customCancelToken = customCancelToken;
  } else if (needCancel) {
    // axios会根据 cancelToken是否存在，进行打断。
    axiosConfig.cancelToken = {};
  }
  if (headers) {
    axiosConfig.headers = headers;
  }

  return axiosConfig;
}
